/**
 * Question-report builder (user request, M11 follow-up): turns a answered
 * question into a plain-text report containing everything needed to debug a
 * wrong question or explanation — the full question, the shown explanation,
 * the user's answer, the correct answer, and non-personal reproduction ids
 * (subtest/difficulty/seed/session/item — all random ids, no personal data).
 * The user's comment is included verbatim.
 */
import type { Question } from '../generators/types.ts'
import type { EquationsPrompt } from '../generators/equations.ts'
import type { LatinPrompt } from '../generators/latin.ts'
import { isRotatable, type FiguresPrompt, type Frame } from '../generators/figures.ts'

export const ISSUE_URL = 'https://github.com/shashank-suresh/dmat-prep/issues'

export interface QuestionReportInput {
  question: Question
  difficulty: string
  /** The saved responses' sub_answers detail (per-letter / per-blank / values). */
  subAnswers: Record<string, unknown>
  /** Human-readable answer strings as shown in the feedback UI. */
  submittedAnswer: string
  rightAnswer: string
  comment: string
  appVersion: string
  userAgent: string
  /** Random ids — non-personal, used only to correlate reports. */
  sessionId: string
  itemId: string
  reportedAt?: string
}

function latinText(prompt: LatinPrompt): string[] {
  const lines = ['      α  β  γ  δ  ε']
  prompt.cells.forEach((row, r) => {
    const cells = row
      .map((cell, c) =>
        r === prompt.question.row && c === prompt.question.col ? '?' : (cell ?? '·'),
      )
      .join('  ')
    lines.push(`${String(r + 1).padStart(2)}    ${cells}`)
  })
  return lines
}

function figureFrameText(frame: Frame): string {
  return frame
    .map((s) => {
      const rot = isRotatable(s.shape) ? ` ${s.orientation * 90}°` : ''
      return `${s.shape}@(${s.row + 1},${s.col + 1}) ${s.colour}${rot}`
    })
    .join('; ')
}

function figuresText(prompt: FiguresPrompt): string[] {
  const lines: string[] = []
  prompt.stem.forEach((frame, i) => lines.push(`frame ${i + 1}: ${figureFrameText(frame)}`))
  for (const blank of [0, 1] as const) {
    lines.push(`blank ${blank + 1} options:`)
    prompt.options[blank].forEach((frame, i) =>
      lines.push(`  option ${i + 1}: ${figureFrameText(frame)}`),
    )
  }
  return lines
}

function questionText(question: Question): string[] {
  if (question.subtest === 'equations') {
    const prompt = question.prompt as EquationsPrompt
    return [...prompt.equations, `letters: ${prompt.letters.join(', ')}`]
  }
  if (question.subtest === 'latin') {
    const prompt = question.prompt as LatinPrompt
    const qPos = `${['α', 'β', 'γ', 'δ', 'ε'][prompt.question.col]}${prompt.question.row + 1}`
    return [...latinText(prompt), `? cell: ${qPos}`]
  }
  const prompt = question.prompt as FiguresPrompt
  return figuresText(prompt)
}

export function buildQuestionReport(input: QuestionReportInput): string {
  const { question, subAnswers, submittedAnswer, rightAnswer, comment } = input
  const reportedAt = input.reportedAt ?? new Date().toISOString()

  const lines: string[] = []
  lines.push('# dMAT practice — question report')
  lines.push('')
  lines.push(`- app version: ${input.appVersion}`)
  lines.push(`- reported at: ${reportedAt}`)
  lines.push(`- user agent: ${input.userAgent}`)
  lines.push(`- session id: ${input.sessionId}`)
  lines.push(`- item id: ${input.itemId}`)
  lines.push(`- subtest: ${question.subtest} · difficulty: ${input.difficulty} · seed: ${question.seed}`)
  lines.push('')

  lines.push('## Question')
  lines.push('')
  for (const line of questionText(question)) lines.push(line)
  lines.push('')

  lines.push('## Explanation shown to the user')
  lines.push('')
  question.explanation.forEach((step, i) => {
    lines.push(`${i + 1}. ${step.text}`)
    for (const line of step.lines ?? []) lines.push(`      ${line}`)
  })
  lines.push('')

  lines.push('## My answer')
  lines.push('')
  lines.push(submittedAnswer)
  lines.push('```json')
  lines.push(JSON.stringify(subAnswers, null, 2))
  lines.push('```')
  lines.push('')

  lines.push('## Correct answer')
  lines.push('')
  lines.push(rightAnswer)
  lines.push('')

  lines.push('## My comment')
  lines.push('')
  lines.push(comment.trim() === '' ? '(no comment given)' : comment)
  lines.push('')
  lines.push(`— filed from ${ISSUE_URL}`)
  return lines.join('\n')
}
